import { css, Global } from '@emotion/core'
import React, { useEffect } from 'react'

/** CSS custom property this component publishes; read by the override below. */
export const STATUS_OVERLAY_TOP_VAR = '--dmc-status-overlay-top'

/** Space, in pixels, left between the bottom of the anchor and the top of the status overlay. */
const OVERLAY_GAP = 12

/**
 * zion-ui pins its StatusOverlay — the popup `useNotifications` raises for errors — to the
 * top-right corner on md+ screens with `top: calc(24px + var(--role-indicator-height, 0px))`,
 * which lands it directly on top of the universal search box in the DMC sub-header and hides
 * the ID the user just looked up.
 *
 * Rendering this alongside the element that holds the search box publishes that element's
 * bottom edge as a CSS custom property and overrides the overlay's `top` with it, so the error
 * popup always opens below the search box instead of over it. zion-ui's own value stays the
 * floor via `max()`, so the overlay keeps its default position when the anchor has scrolled out
 * of view or has not been measured yet.
 *
 * The selector keys off `data-testid="status-overlay"` because that is the only stable hook
 * zion-ui puts on the overlay element — its class name is a linaria content hash that changes
 * between zion-ui releases. `is-large-screen` is the attribute zion-ui sets when the top-right
 * layout is in play; on small screens the overlay sits at the bottom of the viewport, nowhere
 * near the search box, so it is deliberately left alone.
 */
export default function StatusOverlayAnchor({ anchorRef }) {
  useEffect(() => {
    const anchor = anchorRef?.current
    if (!anchor) {
      return undefined
    }

    let frame = null

    const publish = () => {
      frame = null
      const { bottom } = anchor.getBoundingClientRect()
      document.documentElement.style.setProperty(STATUS_OVERLAY_TOP_VAR, `${Math.round(bottom + OVERLAY_GAP)}px`)
    }

    // Scrolling and resizing both move the anchor, so coalesce bursts of them into one measurement.
    const schedulePublish = () => {
      if (frame === null) {
        frame = window.requestAnimationFrame(publish)
      }
    }

    publish()
    window.addEventListener('resize', schedulePublish)
    window.addEventListener('scroll', schedulePublish, { passive: true })
    // The anchor also changes height on its own (the page title wraps, the role indicator banner
    // appears), which moves its bottom edge without any window event firing.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedulePublish)
    observer?.observe(anchor)

    return () => {
      if (frame !== null) {
        window.cancelAnimationFrame(frame)
      }
      window.removeEventListener('resize', schedulePublish)
      window.removeEventListener('scroll', schedulePublish)
      observer?.disconnect()
      document.documentElement.style.removeProperty(STATUS_OVERLAY_TOP_VAR)
    }
  }, [anchorRef])

  return (
    <Global
      styles={css`
        [data-testid='status-overlay'][is-large-screen] {
          top: max(calc(24px + var(--role-indicator-height, 0px)), var(${STATUS_OVERLAY_TOP_VAR}, 0px)) !important;
        }
      `}
    />
  )
}
